import React, { useCallback, useMemo } from "react";
import "./IndividualMonth.scss";
import * as Dfns from "date-fns/fp";
import { DateRange } from "../../../utils/types";
import { OverlayTrigger } from "../../OverlayTrigger";
import { Tooltip } from "react-bootstrap";
import { DateInfo, MonthInfo } from "../DPCalendar";
import { FOCUS_OPTIONS, MONTHS } from "../DatePickerButton/DatePickerConstants";
import {
  CompanyFiscalCal,
  getStartOfFiscalMonthFromInd,
  getStartOfFiscalMonthLabel,
  OUTSIDE_RANGE_ERR_MSG,
} from "@blisspointmedia/bpm-types/dist/FiscalCalendar";

const DOW_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

interface IndividualMonthProps {
  monthInfo: MonthInfo;
  onSelect: (date: string) => void;
  range?: Partial<DateRange>;
  focus?: typeof FOCUS_OPTIONS[number];
  setSelectedMonth: (selectedMonth: Date) => void;
  years: number[];
  maxDate?: string;
  classname?: string;
  fiscalCal: CompanyFiscalCal;
}

export const IndividualMonth: React.FC<IndividualMonthProps> = ({
  monthInfo,
  range,
  onSelect,
  focus,
  setSelectedMonth,
  years,
  maxDate,
  classname,
  fiscalCal,
}) => {
  const dayGrid = useMemo(() => {
    const rows: (DateInfo | null)[][] = [];
    let row: (DateInfo | null)[] = [];
    let lastWasFirst = false;
    for (let dateInfo of monthInfo.dates) {
      if (dateInfo) {
        let info = { ...dateInfo };
        if (lastWasFirst) {
          // This is only necessary when we're on the same row as the start. Thus, if the row has no
          // length, meaning we're the first element, we don't need to set.
          if (row.length) {
            info.lastWasFirst = true;
          }
          lastWasFirst = false;
        }
        if (range) {
          if (info.date === range.start) {
            info.selectedLeft = true;
            lastWasFirst = true;
          } else if (info.date === range.end) {
            info.selectedRight = true;
            const last = row[row.length - 1];
            if (last) {
              last.nextIsLast = true;
            }
          } else if (range.start && range.end && info.date > range.start && info.date < range.end) {
            info.span = true;
          }
        }
        row.push(info);
      } else {
        row.push(null);
      }

      if (row.length === 7) {
        rows.push(row);
        row = [];
      }
    }
    return rows;
  }, [monthInfo, range]);

  const monthValue = useMemo(
    () => Dfns.getMonth(getStartOfFiscalMonthLabel(monthInfo.date, fiscalCal)),
    [monthInfo, fiscalCal]
  );

  const onMonthChange = useCallback(
    (monthStr: string) => {
      try {
        const newMonth = getStartOfFiscalMonthFromInd(
          monthInfo.year,
          parseInt(monthStr),
          fiscalCal
        );
        setSelectedMonth(newMonth);
      } catch {
        console.error(OUTSIDE_RANGE_ERR_MSG);
      }
    },
    [setSelectedMonth, monthInfo, fiscalCal]
  );
  const onYearChange = useCallback(
    (year: string) => {
      try {
        const newMonth = getStartOfFiscalMonthFromInd(parseInt(year), monthValue, fiscalCal);
        setSelectedMonth(newMonth);
      } catch {
        console.error(OUTSIDE_RANGE_ERR_MSG);
      }
    },
    [setSelectedMonth, monthValue, fiscalCal]
  );

  const dowLabels = useMemo(() => {
    const dayLabels: JSX.Element[] = [];
    const sow = fiscalCal.startOfWeek || 0;
    for (let i = sow; i < DOW_LABELS.length + sow; i++) {
      dayLabels.push(<div key={`day-label-${i}`}>{DOW_LABELS[i % 7]}</div>);
    }
    return dayLabels;
  }, [fiscalCal]);

  return (
    <div className={`DPMonth ${classname}`}>
      <div className="monthHeader">
        <select value={monthValue} onChange={e => onMonthChange(e.target.value)}>
          {MONTHS.map((month, i) => (
            <option key={month} value={i}>
              {month}
            </option>
          ))}
        </select>
        <select value={monthInfo.year} onChange={e => onYearChange(e.target.value)}>
          {years.map(year => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
      <div className="daysOfWeek">{dowLabels}</div>
      <div className="dayGrid">
        {dayGrid.map((row, i) => (
          <div key={`row-${i}`}>
            {row.map((day, j) => {
              let classes: string[] = [];
              let display = "";
              let key = `row-${i}-day-${j}`;
              if (day) {
                ({ display } = day);
                key = day.date;
                if (day.invalid) {
                  classes.push("invalid");
                }
                if (focus) {
                  classes.push(focus === "START" ? "focusStart" : "focusEnd", "focus");
                }
                if (day.selectedLeft || day.selectedRight) {
                  classes.push("selected");
                  if (day.selectedLeft && range?.end && !(range?.end === range?.start)) {
                    classes.push("selectedLeft");
                  } else if (day.selectedRight && range?.start && !(range?.end === range?.start)) {
                    classes.push("selectedRight");
                  }
                } else if (day.span) {
                  classes.push("span");
                }
                if (day.lastWasFirst) {
                  classes.push("lastWasFirst");
                }
                if (day.nextIsLast) {
                  classes.push("nextIsLast");
                }
              } else {
                classes.push("notCurrentMonth");
              }

              return day && day.invalid && maxDate && day.date > maxDate ? (
                <OverlayTrigger
                  placement={OverlayTrigger.PLACEMENTS.LEFT.CENTER}
                  overlay={<Tooltip id={day.date}>Data not yet available for these dates.</Tooltip>}
                  key={key}
                >
                  <div
                    className={classes.join(" ")}
                    onClick={() => day && !day.invalid && onSelect(day.date)}
                  >
                    {display}
                  </div>
                </OverlayTrigger>
              ) : (
                <div
                  key={key}
                  className={classes.join(" ")}
                  onClick={() => day && !day.invalid && onSelect(day.date)}
                >
                  {display}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default IndividualMonth;
